import React, { useId, useState } from "react";
import PropTypes from "prop-types";
import { X, ChevronDown, ChevronUp } from "lucide-react";

import "./FormLayout.css";

export function FormLayout({
  title,
  description,
  icon,
  children,
  footer,
  danger = false,
  iconStatus = "",
  formLayoutStyle,
  className = "",
  onClose,
  collapsible = false,
  defaultOpen = true,
  expanded,
  onExpandedChange,
  padding,
  background,
}) {
  const titleId = useId();
  const descriptionId = useId();
  const contentId = useId();

  const [internalExpanded, setInternalExpanded] = useState(defaultOpen);

  const isControlled = typeof expanded === "boolean";

  const isExpanded = collapsible
    ? isControlled
      ? expanded
      : internalExpanded
    : true;

  const hasHeader = Boolean(title || description || icon || onClose);
  const hasBody = children != null || footer != null;

  const layoutClasses = [
    "katch-form-layout",
    danger && "katch-form-layout--danger",
    collapsible && "katch-form-layout--collapsible",
    collapsible && !isExpanded && "katch-form-layout--collapsed",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const iconFrameClasses = [
    "katch-form-layout__icon-frame",
    danger && "katch-form-layout__icon-frame--danger",
    iconStatus && `katch-form-layout__icon-frame--${iconStatus}`,
  ]
    .filter(Boolean)
    .join(" ");

  const iconClasses = [
    "katch-form-layout__icon",
    danger && "katch-form-layout__icon--danger",
    iconStatus && `katch-form-layout__icon--${iconStatus}`,
  ]
    .filter(Boolean)
    .join(" ");

  const layoutStyle = {
    ...(background !== undefined ? { background } : {}),
    ...(padding !== undefined ? { padding } : {}),
    ...formLayoutStyle,
  };

  const handleToggle = () => {
    if (!collapsible) return;

    const nextExpanded = !isExpanded;

    if (!isControlled) {
      setInternalExpanded(nextExpanded);
    }

    onExpandedChange?.(nextExpanded);
  };

  return (
    <section
      className={layoutClasses}
      style={layoutStyle}
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={description ? descriptionId : undefined}
    >
      {hasHeader && (
        <header className="katch-form-layout__header">
          {icon && (
            <div className={iconFrameClasses} aria-hidden="true">
              <span className={iconClasses}>{icon}</span>
            </div>
          )}

          {(title || description || collapsible) && (
            <div className="katch-form-layout__title-container">
              {collapsible ? (
                <h2 id={titleId} className="katch-form-layout__title">
                  <button
                    type="button"
                    className="katch-form-layout__toggle"
                    onClick={handleToggle}
                    aria-expanded={isExpanded}
                    aria-controls={hasBody ? contentId : undefined}
                  >
                    <span className="katch-form-layout__toggle-label">
                      {title}
                    </span>

                    {isExpanded ? (
                      <ChevronUp size={18} aria-hidden="true" />
                    ) : (
                      <ChevronDown size={18} aria-hidden="true" />
                    )}
                  </button>
                </h2>
              ) : (
                title && (
                  <h2 id={titleId} className="katch-form-layout__title">
                    {title}
                  </h2>
                )
              )}

              {description && (
                <p
                  id={descriptionId}
                  className="katch-form-layout__description"
                >
                  {description}
                </p>
              )}
            </div>
          )}

          {onClose && (
            <button
              type="button"
              className="katch-form-layout__close-btn"
              onClick={onClose}
              aria-label="Close form"
            >
              <X size={20} aria-hidden="true" />
            </button>
          )}
        </header>
      )}

      {hasBody && (
        <div
          id={contentId}
          className="katch-form-layout__body"
          hidden={!isExpanded}
        >
          {children != null && (
            <div className="katch-form-layout__content">{children}</div>
          )}

          {footer != null && (
            <footer className="katch-form-layout__footer">{footer}</footer>
          )}
        </div>
      )}
    </section>
  );
}

FormLayout.propTypes = {
  title: PropTypes.node,
  description: PropTypes.node,
  icon: PropTypes.node,
  children: PropTypes.node,
  footer: PropTypes.node,
  danger: PropTypes.bool,
  iconStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  formLayoutStyle: PropTypes.object,
  className: PropTypes.string,
  onClose: PropTypes.func,
  collapsible: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  expanded: PropTypes.bool,
  onExpandedChange: PropTypes.func,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  background: PropTypes.string,
};
